import { Injectable } from '@angular/core';
import { Params } from '@angular/router';

import {
  ActivatedRouterStateSnapshot,
  ProductSearchPage,
  PageType,
  RoutingService,
  Breadcrumb,
  Facet
} from '@spartacus/core';
import { FacetList, ProductFacetService, ProductListComponentService } from '@spartacus/storefront';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { DpSearchService } from '@app/core/services/search.service';
import { DpBreadcrumb } from '@app/core/types/backend/dp-breadcrumb';

import { FacetCategoryList } from '../types';

@Injectable({
  providedIn: 'root'
})
export class DpProductFacetService extends ProductFacetService {
  readonly facetCategoryList$: Observable<FacetCategoryList> = this.searchResult$.pipe(
    map((result: ProductSearchPage) => ({
      facetCategories: result.facetCategories,
      activeFacets: result.breadcrumbs
    }))
  );

  readonly facetList$: Observable<FacetList> = this.searchResult$.pipe(
    map((result: ProductSearchPage) => ({
      facets: result?.facetCategories?.reduce(
        (previousValue, currentValue) => [...previousValue, ...currentValue.facets],
        [] as Facet[]
      ),
      activeFacets: result.breadcrumbs
    }))
  );

  constructor(
    routing: RoutingService,
    productListComponentService: ProductListComponentService,
    private readonly dpSearchService: DpSearchService
  ) {
    super(routing, productListComponentService);
  }

  /**
   * Filters the current result by verifying if the result is related to the page.
   * This is done to avoid a combination of the next page and the current search results.
   */
  protected filterForPage(state: ActivatedRouterStateSnapshot, page: ProductSearchPage): boolean {
    if (!page.currentQuery?.query?.value) {
      return false;
    }

    if (state.context.type === PageType.CATEGORY_PAGE) {
      return page.currentQuery.query.value.indexOf(`allCategories:${state.context.id}`) > -1;
    }

    if (state.context.type === PageType.PRODUCT_PAGE) {
      return true;
    }

    if (state.context.type === PageType.CONTENT_PAGE && state.context.id === 'search') {
      const query = this.dpSearchService.escapeQuery(state.params.query);
      const queryEscapedTerm = this.dpSearchService.escapeTerm(state.params.query);
      return (
        page.currentQuery.query.value.startsWith(`${query}:`) ||
        page.currentQuery.query.value.startsWith(`${queryEscapedTerm}:`)
      );
    }

    return false;
  }

  protected filterBreadcrumbs(breadcrumbs: DpBreadcrumb[], params: Params): Breadcrumb[] {
    return breadcrumbs
      ? breadcrumbs.filter(
          (breadcrumb) =>
            !(
              breadcrumb.facetCode === 'allCategories' &&
              (breadcrumb.facetValueCode === params.categoryCode ||
                breadcrumb.facetValueCode === params.brandCode ||
                breadcrumb.breadcrumbPath)
            )
        )
      : [];
  }
}
